import Dexie from 'dexie';
import type { HangerCard, PhotoFile } from './MannequinContext';

const TTL_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

interface SerializedPhoto {
    id: string;
    url: string;
    preview: string;
    type?: 'model' | 'product';
    name?: string;
}

interface SerializedHangerCard {
    id: string;
    photos: SerializedPhoto[];
    config: HangerCard['config'];
    selectedModel: HangerCard['selectedModel'];
    sourceImage?: { preview: string; mimeType: string } | null;
    generationStatus: HangerCard['generationStatus'];
    predictionId?: string | null;
    progress?: number;
    pollingAttempts?: number;
    generatedResults: HangerCard['generatedResults'];
    errorMessage?: string;
}

interface DraftRecord {
    key: string;
    savedAt: number;
    cards: SerializedHangerCard[];
}

class HangerCardsDraftDb extends Dexie {
    drafts!: Dexie.Table<DraftRecord, string>;

    constructor() {
        super('hangerCardsDraftDb');
        this.version(1).stores({
            drafts: 'key',
        });
    }
}

const db = new HangerCardsDraftDb();

const DRAFT_KEY = 'singleton';

function serializeForStorage(cards: HangerCard[]): SerializedHangerCard[] {
    return cards.map(card => ({
        id: card.id,
        photos: card.photos.map(p => ({
            id: p.id,
            url: p.url,
            preview: p.preview,
            type: p.type,
            name: p.name,
            size: p.size || p.file?.size,
        })),
        config: { ...card.config },
        selectedModel: card.selectedModel === 'EachLabs' ? 'GoogleGemini' : card.selectedModel,
        sourceImage: card.sourceImage ? { preview: card.sourceImage.preview, mimeType: card.sourceImage.mimeType } : null,
        generationStatus: card.generationStatus,
        predictionId: card.predictionId,
        progress: card.progress,
        pollingAttempts: card.pollingAttempts,
        generatedResults: card.generatedResults,
        errorMessage: card.errorMessage,
    }));
}

function restoreCards(serialized: SerializedHangerCard[]): HangerCard[] {
    return serialized.map(card => ({
        ...card,
        photos: card.photos.map((p): PhotoFile => ({
            id: p.id,
            file: null,
            url: p.url,
            preview: p.preview || p.url || '',
            type: p.type,
            name: p.name,
            size: (p as any).size,
        })),
        sourceImage: card.sourceImage || null,
    }));
}

export async function saveHangerCardsDraft(cards: HangerCard[]): Promise<void> {
    try {
        const record: DraftRecord = {
            key: DRAFT_KEY,
            savedAt: Date.now(),
            cards: serializeForStorage(cards),
        };
        await db.drafts.put(record);
    } catch (error) {
        console.error('Failed to save hanger cards to IndexedDB:', error);
    }
}

export async function loadHangerCardsDraft(): Promise<HangerCard[]> {
    try {
        const record = await db.drafts.get(DRAFT_KEY);
        if (!record) return [];

        if (Date.now() - record.savedAt > TTL_MS) {
            await db.drafts.delete(DRAFT_KEY);
            return [];
        }

        return restoreCards(record.cards);
    } catch (error) {
        console.error('Failed to load hanger cards from IndexedDB:', error);
        return [];
    }
}

export async function clearHangerCardsDraft(): Promise<void> {
    try {
        await db.drafts.delete(DRAFT_KEY);
    } catch (error) {
        console.error('Failed to clear hanger cards from IndexedDB:', error);
    }
}
